/**
 * <hana-chatbot> Custom Element
 *
 * Web Component that renders the HANA chatbot widget inside Shadow DOM.
 * Supports configuration via HTML attributes or the JS init API.
 *
 * Usage (HTML attributes):
 *   <hana-chatbot entity-id="eec" api-endpoint="https://api.example.com"></hana-chatbot>
 *
 * Usage (JS API):
 *   HanaWidget.init({ entityId: 'eec', apiEndpoint: '...', branding: {...} })
 */

import type { WidgetConfig, ResolvedWidgetConfig } from "./types";
import { resolveConfig } from "./config";
import { ChatWidget } from "./components/ChatWidget";
import { getWidgetStyles } from "./styles/widget.css";
import { injectOutfitFont, requiresOutfitFont } from "./styles/fonts";

export class HanaChatbotElement extends HTMLElement {
  private shadow: ShadowRoot;
  private widget: ChatWidget | null = null;
  private resolvedConfig: ResolvedWidgetConfig | null = null;

  /** Attributes that trigger re-initialization when changed */
  static get observedAttributes(): string[] {
    return [
      "entity-id",
      "api-endpoint",
      "test-harness",
      "harness-session-id",
      "position",
      // REQ-D-046 — shell-variant attributes also retrigger init when
      // the embedder swaps them (e.g. an admin toggle for testing).
      "ui-mode",
      "default-size",
      "allow-expand",
      "trigger-cta-selector",
      // Per-embed launcher mark; re-init on change so an embedder can
      // swap the image without reloading the host page.
      "launcher-icon",
    ];
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    // If already initialized via JS API, do nothing
    if (this.widget) return;

    // Try to initialize from HTML attributes
    const entityId = this.getAttribute("entity-id");
    const apiEndpoint = this.getAttribute("api-endpoint");

    if (entityId && apiEndpoint) {
      this.initFromAttributes(entityId, apiEndpoint);
    }

    // Spec: specs/acceptance-test-harness.md §3.2 + §4.4 SS-4.
    // The test-harness attribute alone enables NOTHING — the harness session
    // cookie minted by /api/acceptance/test-harness/bootstrap is the actual
    // capability. This proxy gives the parent admin page a typed surface for
    // calling the inject route handler.
    if (this.getAttribute("test-harness") === "1") {
      installTestHarnessProxy();
    }
  }

  disconnectedCallback(): void {
    this.destroyWidget();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;
    if (!this.isConnected) return;

    // Re-initialize if entity / endpoint / harness binding changes.
    // harness-session-id rotation (e.g. on /reset) needs a clean WS
    // reconnect so the new binding ID is in the URL query string.
    // REQ-D-046 — shell-variant attribute changes also rebuild so the
    // panel/modal mode + size + allow-expand stay consistent.
    if (
      name === "entity-id" ||
      name === "api-endpoint" ||
      name === "harness-session-id" ||
      name === "position" ||
      name === "ui-mode" ||
      name === "default-size" ||
      name === "allow-expand" ||
      name === "trigger-cta-selector"
    ) {
      const entityId = this.getAttribute("entity-id");
      const apiEndpoint = this.getAttribute("api-endpoint");
      if (entityId && apiEndpoint) {
        this.destroyWidget();
        this.initFromAttributes(entityId, apiEndpoint);
      }
    }
  }

  /**
   * Initialize the widget from a JS config object.
   * Called by HanaWidget.init().
   */
  initFromConfig(config: WidgetConfig): void {
    this.destroyWidget();
    this.resolvedConfig = resolveConfig(config);
    this.render();
  }

  /**
   * Open the chat panel programmatically.
   */
  open(): void {
    this.widget?.open();
  }

  /**
   * Close the chat panel programmatically.
   */
  close(): void {
    this.widget?.close();
  }

  /**
   * Get the resolved configuration (for testing/debugging).
   */
  getConfig(): ResolvedWidgetConfig | null {
    return this.resolvedConfig;
  }

  /**
   * Get the internal ChatWidget instance (for testing/extension).
   */
  getChatWidget(): ChatWidget | null {
    return this.widget;
  }

  /**
   * Switch the widget theme to a different entity preset at runtime.
   * Rebuilds the widget with the new entity's branding without
   * requiring a full page reload.
   *
   * @param entityId - Entity identifier (e.g., "eec", "dccs")
   */
  setTheme(entityId: string): void {
    if (!this.resolvedConfig) return;

    // V0.6 Finding 1 fix: do NOT pre-fill `branding` with generic
    // `welcomeMessage` / `displayName` / `primaryColor` here.
    // `resolveConfig` already performs the merge:
    //   DEFAULT_BRANDING < ENTITY_PRESETS[entityId] < config.branding
    // Pre-filling here puts the generic strings in `config.branding`,
    // which is the LAST layer in the merge — it would override the
    // entity preset and ship the generic "healthcare navigation
    // assistant" welcome to every entity. resolveConfig handles the
    // entity-preset lookup; pass an empty branding to let it through.
    const config: WidgetConfig = {
      entityId,
      apiEndpoint: this.resolvedConfig.apiEndpoint,
      branding: {},
    };

    this.initFromConfig(config);
  }

  private initFromAttributes(entityId: string, apiEndpoint: string): void {
    // Parse optional theme JSON attribute
    let branding: Record<string, unknown> = {};
    const themeAttr = this.getAttribute("theme");
    if (themeAttr) {
      try {
        branding = JSON.parse(themeAttr);
      } catch {
        // Invalid JSON in theme attribute — use defaults
        console.warn(
          "[HANA Widget] Invalid JSON in theme attribute, using defaults",
        );
      }
    }

    // Per-embed launcher mark (`data-launcher-icon` on the script tag).
    // Applied after `theme` so the dedicated attribute wins over a
    // `launcherIconUrl` embedded in the theme JSON — the script-tag
    // attribute is the documented surface. Validated in ToggleButton.
    const launcherIcon = this.getAttribute("launcher-icon");
    if (launcherIcon) {
      branding.launcherIconUrl = launcherIcon;
    }

    // Optional harness binding ID — present when the widget is mounted
    // under /admin/acceptance/live; absent for production end-user
    // mounts. Forwarded to the WebSocket as a query param so the
    // backend chat handler can publish trace events back to the
    // bound admin's SSE stream.
    const harnessSessionId =
      this.getAttribute("harness-session-id") ?? undefined;

    // Spec §4.2 SS-2: the `test-harness="1"` attribute marks every
    // outbound REST call as `X-Hana-Env: sandbox` so the backend env
    // banner + integration adapters honour sandbox posture (suppressed
    // Chatwoot escalation, blocked HMI One writes, sandbox LLM key,
    // etc.). The attribute alone is the env signal; the cookie is
    // still the authentication capability per §4.4 SS-4.
    const testHarness = this.getAttribute("test-harness") === "1";

    // Optional launcher-position override (e.g., `position="bottom-left"` when
    // mounted inside a right-anchored drawer so the floating toggle doesn't
    // overlap the drawer's controls). Parsed against the same enum the
    // script-tag `data-position` attribute consumes — see `config/defaults.ts`.
    const positionAttr = this.getAttribute("position");
    let position: WidgetConfig["position"] = undefined;
    if (positionAttr === "bottom-left" || positionAttr === "bottom-right") {
      position = positionAttr;
    } else if (positionAttr !== null) {
      console.warn(
        `[HANA Widget] Invalid position="${positionAttr}". ` +
          `Expected "bottom-left" or "bottom-right". Falling back to default.`,
      );
    }

    // REQ-D-046 — shell-variant attributes. resolveConfig validates
    // each value + emits console.warn on unknown enum, so a typo here
    // never bricks the embed.
    const uiModeAttr = this.getAttribute("ui-mode");
    const defaultSizeAttr = this.getAttribute("default-size");
    const allowExpandAttr = this.getAttribute("allow-expand");
    const triggerCtaSelectorAttr = this.getAttribute("trigger-cta-selector");
    const uiMode =
      uiModeAttr === "panel" || uiModeAttr === "modal" ? uiModeAttr : undefined;
    const defaultSize =
      defaultSizeAttr === "compact" ||
      defaultSizeAttr === "expanded" ||
      defaultSizeAttr === "fullscreen"
        ? defaultSizeAttr
        : undefined;
    const allowExpand =
      allowExpandAttr === "true"
        ? true
        : allowExpandAttr === "false"
          ? false
          : undefined;

    // V0.6 Finding 1 fix: do NOT pre-fill `branding` here with generic
    // `welcomeMessage` / `displayName` / `primaryColor`.
    // `resolveConfig`'s merge order is:
    //   DEFAULT_BRANDING < ENTITY_PRESETS[entityId] < config.branding
    // — `config.branding` is the LAST layer. Pre-filling it with the
    // generic "healthcare navigation assistant" welcome would override
    // every entity preset. The user's `theme` attribute (parsed into
    // `branding` above) IS the only legitimate per-mount override and
    // gets passed through unchanged; DEFAULT_BRANDING already provides
    // a fallback when no entity preset matches.
    const config: WidgetConfig = {
      entityId,
      apiEndpoint,
      branding,
      harnessSessionId,
      testHarness: testHarness || undefined,
      position,
      uiMode,
      defaultSize,
      allowExpand,
      triggerCtaSelector: triggerCtaSelectorAttr,
    };

    this.resolvedConfig = resolveConfig(config);
    this.render();
  }

  private render(): void {
    if (!this.resolvedConfig) return;

    // Inject styles
    const styleEl = document.createElement("style");
    styleEl.textContent = getWidgetStyles();
    this.shadow.appendChild(styleEl);

    // Inject Outfit font if required by the entity's font family
    if (requiresOutfitFont(this.resolvedConfig.branding.fontFamily)) {
      injectOutfitFont(this.shadow);
    }

    // Create and mount widget
    this.widget = new ChatWidget(this, this.resolvedConfig);
    this.shadow.appendChild(this.widget.getElement());

    // REQ-A-020 / #880 — if the panel was open when the patient navigated,
    // reopen it so the replayed transcript is visible rather than hidden
    // behind the bubble. Per-tab, so a later visit still starts closed.
    this.widget.restorePanelState();

    // REQ-A-020 / #880 — and actually go and fetch that transcript. Panel
    // state was being restored while the conversation was not: the widget
    // waited for the patient to speak before it would even try to resume,
    // so an open panel came back EMPTY and asked for the Terms again.
    // Ordered after restorePanelState so the panel is already visible when
    // the replayed turns arrive; no-op unless a resume ticket is held.
    this.widget.restoreConversation();

    // REQ-D-033: wire feedback submissions to the structured backend
    // logger via POST /api/chat/feedback. The brief defers the
    // "Vercel" analytics destination to ops; the structured backend
    // store (FeedbackStore -> DataFlow + INFO log line) is the
    // authoritative sink for Wave 1.
    this.wireFeedbackPipeline(this.resolvedConfig);
  }

  /**
   * REQ-D-033 — route widget feedback events to the backend
   * /api/chat/feedback endpoint. The endpoint persists to FeedbackStore
   * AND emits a PII-masked INFO log line; downstream analytics (Vercel
   * or otherwise) is an ops-side decision and consumes the same
   * structured log stream.
   *
   * Failures are best-effort: feedback is non-critical, so we swallow
   * network errors after logging them to the console — the user's
   * conversation MUST NOT degrade because the analytics endpoint is
   * unreachable.
   */
  private wireFeedbackPipeline(config: ResolvedWidgetConfig): void {
    if (!this.widget) return;
    this.widget.onFeedbackSubmit({
      onSubmit: (event) => {
        const url = `${config.apiEndpoint.replace(/\/$/, "")}/api/chat/feedback`;
        // Ratings are 1-5 stars OR null (skipped). Backend rejects
        // payloads with neither rating nor comment; the widget UX
        // currently never produces both empty so the call is safe.
        const payload: Record<string, unknown> = {
          rating:
            event.stars ??
            (event.rating === "positive"
              ? 5
              : event.rating === "negative"
                ? 1
                : null),
          comment: "",
          conversation_id: event.conversationId,
          entity_id: event.entityId,
          submitted_at: event.timestamp,
        };
        try {
          // Fire-and-forget — feedback is non-blocking. credentials
          // included so the session cookie/JWT rides along; the
          // backend handler validates the session and rejects
          // unauthenticated submits with 401.
          void fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
            keepalive: true,
          }).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn("[hana-widget] feedback submit failed", err);
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[hana-widget] feedback submit threw", err);
        }
      },
    });
  }

  private destroyWidget(): void {
    if (this.widget) {
      this.widget.destroy();
      this.widget = null;
    }
    this.resolvedConfig = null;

    // Clear shadow DOM contents
    while (this.shadow.firstChild) {
      this.shadow.removeChild(this.shadow.firstChild);
    }
  }
}

// ---------------------------------------------------------------------------
// Test-harness proxy
//
// Spec: specs/acceptance-test-harness.md §3.2 + §4.4 SS-4.
//
// Exposes a narrow JS surface on `window.hanaTestHarness` when the widget is
// mounted with `test-harness="1"`. The proxy delegates to the admin app's
// Next.js route handler (`/api/acceptance/test-harness/inject`), which is
// the ONLY path that has access to the HttpOnly harness cookie.
//
// The widget JS NEVER reads the harness token directly (per spec
// "the cookie is the capability; the query string alone enables NOTHING").
// Validation runs server-side against the shipped HarnessInjection allowlist
// (src/hana/security/injection_allowlist.py).
// ---------------------------------------------------------------------------

interface HanaTestHarnessAPI {
  readonly version: string;
  injectState(payload: Record<string, unknown>): Promise<unknown>;
}

const HARNESS_API_VERSION = "2.1.0";

function installTestHarnessProxy(): void {
  // Idempotent — multiple <hana-chatbot test-harness="1"> instances on the
  // same page share one proxy. The version constant lets the admin page
  // verify compat (e.g. before calling injectState).
  const w = globalThis as unknown as {
    hanaTestHarness?: HanaTestHarnessAPI;
  };
  if (w.hanaTestHarness && w.hanaTestHarness.version === HARNESS_API_VERSION) {
    return;
  }
  w.hanaTestHarness = {
    version: HARNESS_API_VERSION,
    injectState: async (payload) => {
      const res = await fetch("/api/acceptance/test-harness/inject", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload ?? {}),
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail =
          typeof body === "object" && body && "detail" in body
            ? String((body as Record<string, unknown>).detail)
            : `inject failed (${res.status})`;
        throw new Error(detail);
      }
      return body;
    },
  };
}
